import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import { EventEmitter, forwardRef, Input, Output, Directive } from '@angular/core';

export function GetValueAccessorProvider(type: any) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => type),
        multi: true
    };
}

@Directive({ standalone: false })
export class ValueAccessorBase<T> implements ControlValueAccessor {
    private innerValue: T;

    private changed = new Array<(value: T) => void>();
    private touched = new Array<() => void>();
    selectedItem: any;
    @Input() showTitle: any;
    @Input() disabled = false;

    @Output() valueChange = new EventEmitter<T>();

    get value(): T {
        return this.innerValue;
    }

    @Input()
    set value(value: T) {
        if (this.innerValue !== value) {
            this.writeValue(value);
            this.changed.forEach(f => f(value));
            this.valueChange.emit(value);
        }
    }

    touch() {
        this.touched.forEach(f => f());
    }

    writeValue(value: T) {
        //this method is used when initializing component with first values
        //therefore don't raise changed event
        if (value == null || this.innerValue !== value) {
            if (this.selectedItem) {
                this.selectedItem = null;
                this.showTitle = null;
            }
        }

        this.innerValue = value;
    }

    /** Numeric `0` / string ids must compare equal so label sync survives re-select and ngModel re-sync. */
    protected valuesEqual(a: T, b: T): boolean {
        if (a === b) {
            return true;
        }
        if (a == null || b == null) {
            return false;
        }
        if (typeof a === 'object' || typeof b === 'object') {
            return false;
        }
        // Number('') is 0, so an empty string must not count as numerically equal to 0.
        const na = String(a).trim() === '' ? NaN : Number(a as unknown);
        const nb = String(b).trim() === '' ? NaN : Number(b as unknown);
        if (!Number.isNaN(na) && !Number.isNaN(nb) && na === nb) {
            return true;
        }
        return String(a) === String(b);
    }

    registerOnChange(fn: (value: T) => void) {
        this.changed.push(fn);
    }

    registerOnTouched(fn: () => void) {
        this.touched.push(fn);
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
